/**
 * Client-side Environment Variable Validator
 * Ensures no sensitive secrets are exposed to the client
 */

// NOTE: Cannot import logger here - client-env-validator runs before logger is initialized
// Use console methods for early startup diagnostics

interface ClientEnvConfig {
  name: string;
  required: boolean;
  validation?: (value: string) => boolean;
  description: string;
}

/**
 * Whitelist of environment variables that are safe to expose client-side
 * All variables must start with VITE_ to be available in the client
 */
const SAFE_CLIENT_ENV_VARS: Record<string, ClientEnvConfig> = {
  VITE_SUPABASE_URL: {
    name: 'VITE_SUPABASE_URL',
    required: true,
    // The dev server (never a production build) may also use a loopback local Supabase stack, as the
    // CI e2e job and dev203 do (CS-304).
    validation: (value) =>
      (value.startsWith('https://') && value.includes('supabase')) ||
      (import.meta.env.DEV === true && /^http:\/\/(127\.0\.0\.1|localhost):\d+$/.test(value)),
    description: 'Supabase project URL (safe to expose)'
  },
  VITE_SUPABASE_ANON_KEY: {
    name: 'VITE_SUPABASE_ANON_KEY',
    required: true,
    validation: (value) => value.startsWith('eyJ') && value.length > 100,
    description: 'Supabase anonymous key (safe to expose, has limited permissions)'
  },
  VITE_STRIPE_PUBLISHABLE_KEY: {
    name: 'VITE_STRIPE_PUBLISHABLE_KEY',
    required: false,
    validation: (value) => value.startsWith('pk_'),
    description: 'Stripe publishable key (safe to expose)'
  },
  VITE_AUTH_MODE: {
    name: 'VITE_AUTH_MODE',
    required: false,
    validation: (value) => ['dev', 'real', 'bypass'].includes(value),
    description: 'Authentication mode for development'
  },
  VITE_SESSION_TIMEOUT: {
    name: 'VITE_SESSION_TIMEOUT',
    required: false,
    validation: (value) => !isNaN(Number(value)) && Number(value) > 0,
    description: 'Session timeout in milliseconds'
  },
  VITE_ENABLE_SECURITY_VALIDATION: {
    name: 'VITE_ENABLE_SECURITY_VALIDATION',
    required: false,
    validation: (value) => ['true', 'false'].includes(value),
    description: 'Enable security validation features'
  },
  VITE_FEATURE_COACH_PROGRESS_METRICS: {
    name: 'VITE_FEATURE_COACH_PROGRESS_METRICS',
    required: false,
    validation: (value) => ['true', 'false'].includes(value),
    description: 'Feature flag to enable the new Coach Progress metrics experience'
  },

};

/**
 * List of environment variable patterns that should NEVER be exposed client-side
 */
const FORBIDDEN_CLIENT_PATTERNS = [
  /.*SECRET.*/i,
  /.*PRIVATE.*/i,
  /.*API_KEY$/i,
  /OPENAI_API_KEY/i,
  /STRIPE_SECRET/i,
  /.*SERVICE_ROLE.*/i,
  /.*WEBHOOK_SECRET.*/i,
  /RESEND_API_KEY/i,
  /.*_KEY$/i, // Any variable ending in _KEY (except whitelisted ones)
];

/**
 * Validate that environment variable is safe for client exposure
 */
function isVariableSafeForClient(name: string, value: string): { safe: boolean; reason?: string } {
  // Must start with VITE_ to be intentionally exposed
  if (!name.startsWith('VITE_')) {
    return { 
      safe: false, 
      reason: `Variable '${name}' does not start with VITE_ prefix` 
    };
  }
  
  // Check whitelist first - whitelisted variables are always safe
  if (SAFE_CLIENT_ENV_VARS[name]) {
    return { safe: true };
  }
  
  // For non-whitelisted variables, check against forbidden patterns
  for (const pattern of FORBIDDEN_CLIENT_PATTERNS) {
    if (pattern.test(name)) {
      return { 
        safe: false, 
        reason: `Variable '${name}' matches forbidden pattern and may contain sensitive data` 
      };
    }
  }
  
  // Not in whitelist and doesn't match forbidden patterns
  return { 
    safe: false, 
    reason: `Variable '${name}' is not in the approved whitelist` 
  };
}

/**
 * Get a client environment variable safely
 */
export function getClientEnv(name: string): string | undefined {
  const value = import.meta.env[name];
  
  if (!value) {
    return undefined;
  }
  
  const safety = isVariableSafeForClient(name, value);
  if (!safety.safe) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console, no-restricted-syntax -- Pre-logger startup diagnostics, cannot import logger (circular dependency)
      console.error('[ClientEnvValidator] Unsafe environment variable access denied:', safety.reason);
    }
    throw new Error(`Access denied: ${safety.reason}`);
  }
  
  const config = SAFE_CLIENT_ENV_VARS[name];
  if (config?.validation && !config.validation(value)) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console, no-restricted-syntax -- Pre-logger startup diagnostics, cannot import logger (circular dependency)
      console.error('[ClientEnvValidator] Environment variable validation failed:', name);
    }
    throw new Error(`Invalid value for environment variable '${name}'`);
  }
  
  return value;
}

/**
 * Validate all client environment variables at startup
 */
export function validateClientEnvironment(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check all required variables
  for (const [name, config] of Object.entries(SAFE_CLIENT_ENV_VARS)) {
    if (config.required) {
      try {
        const value = getClientEnv(name);
        if (!value) {
          errors.push(`Required environment variable '${name}' is not set`);
        }
      } catch (error) {
        errors.push(`Environment variable '${name}': ${error.message}`);
      }
    }
  }
  
  // Scan all available environment variables for potential leaks
  const allEnvVars = import.meta.env;
  for (const [name, value] of Object.entries(allEnvVars)) {
    if (typeof value === 'string' && value.length > 0) {
      const safety = isVariableSafeForClient(name, value);
      if (!safety.safe && !name.startsWith('VITE_') && !name.includes('NODE_')) {
        // Don't add to errors for non-VITE vars as they shouldn't be accessible anyway
      }
    }
  }
  
  const isValid = errors.length === 0;

  if (!isValid && import.meta.env.DEV) {
    // eslint-disable-next-line no-console, no-restricted-syntax -- Pre-logger startup diagnostics, cannot import logger (circular dependency)
    console.error('[ClientEnvValidator] Client environment validation failed:', {
      errorCount: errors.length,
      errors: errors.slice(0, 3)
    });
  }

  return { isValid, errors };
}

/**
 * Production-safe environment configuration
 */
export const CLIENT_CONFIG = {
  supabase: {
    url: getClientEnv('VITE_SUPABASE_URL'),
    anonKey: getClientEnv('VITE_SUPABASE_ANON_KEY'),
  },
  stripe: {
    publishableKey: getClientEnv('VITE_STRIPE_PUBLISHABLE_KEY'),
  },
  auth: {
    mode: getClientEnv('VITE_AUTH_MODE') || 'real',
    sessionTimeout: Number(getClientEnv('VITE_SESSION_TIMEOUT')) || 86400000,
  },
  development: {
    enableSecurityValidation: getClientEnv('VITE_ENABLE_SECURITY_VALIDATION') !== 'false',
  },
  features: {
    coachProgressMetrics: getClientEnv('VITE_FEATURE_COACH_PROGRESS_METRICS') === 'true',
  }
} as const;

/**
 * Runtime check for development mode
 */
export function isDevelopmentMode(): boolean {
  return import.meta.env.MODE === 'development' || import.meta.env.DEV === true;
}

/**
 * Runtime check for production mode
 */
export function isProductionMode(): boolean {
  return import.meta.env.MODE === 'production' || import.meta.env.PROD === true;
}

/**
 * Initialize client environment validation
 * Should be called at application startup
 */
export function initializeClientEnvironment(): void {
  const validation = validateClientEnvironment();
  
  if (!validation.isValid) {
    if (isProductionMode()) {
      // In production, halt execution for security
      throw new Error(`Client environment validation failed: ${validation.errors.join(', ')}`);
    }
    // In development, validation errors already logged by validateClientEnvironment()
  }
  
  // Additional production-specific checks
  if (isProductionMode()) {
    if (CLIENT_CONFIG.auth.mode === 'dev' || CLIENT_CONFIG.auth.mode === 'bypass') {
      throw new Error('Development authentication mode detected in production build');
    }
  }
}